import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import firebaseService from '../services/firebaseService';
import { formatCurrency, calculateOutputGST } from '../services/gstCalculations';
import { generateInvoice } from '../services/invoiceGenerator';
import { getAvailableProducts } from '../services/inventoryService';
import { useAuth } from '../contexts/AuthContext';
import { 
  PlusIcon, 
  DocumentArrowUpIcon, 
  EyeIcon, 
  PencilIcon, 
  TrashIcon,
  CurrencyRupeeIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

function Sales() {
  const { hasPermission, currentUser } = useAuth();
  const [sales, setSales] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [filters, setFilters] = useState({
    quarter: '',
    year: '',
    paymentMethod: ''
  });

  const [formData, setFormData] = useState({
    saleDate: new Date(),
    customerName: '',
    invoiceNumber: '',
    saleAmount: '',
    isGstInclusive: true,
    paymentMethod: 'Cash',
    description: '',
    productName: '',
    imeiNumber: '',
    customerPhone: '',
    customerEmail: '',
    selectedProductId: '',
    discount: 0
  });

  const [invoiceFile, setInvoiceFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const paymentMethods = ['Cash', 'Credit Card', 'UPI', 'Self Finance'];

  useEffect(() => {
    loadSales();
    loadAvailableProducts();
  }, [filters, currentUser]);

  const loadSales = async () => {
    try {
      setLoading(true);
      const salesData = await firebaseService.getSales(filters);
      setSales(salesData);
    } catch (error) {
      console.error('Error loading sales:', error);
      toast.error('Failed to load sales');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableProducts = async () => {
    if (!currentUser) return;
    
    try {
      const products = await getAvailableProducts(currentUser.uid);
      setAllProducts(products);
      setAvailableProducts(products);
    } catch (error) {
      console.error('Error loading available products:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      saleDate: new Date(),
      customerName: '',
      invoiceNumber: '',
      saleAmount: '',
      isGstInclusive: true,
      paymentMethod: 'Cash',
      description: '',
      productName: '',
      imeiNumber: '',
      customerPhone: '',
      customerEmail: '',
      selectedProductId: '',
      discount: 0
    });
    setInvoiceFile(null);
    setEditingSale(null);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.saleAmount) {
      return toast.error('Please enter the sale amount');
    }

    if (parseFloat(formData.saleAmount) <= 0) {
      return toast.error('Sale amount must be greater than zero');
    }

    setSubmitting(true);

    try {
      const saleData = {
        ...formData,
        saleAmount: parseFloat(formData.saleAmount),
        customerName: formData.customerName || 'Cash Customer'
      };

      if (editingSale) {
        await firebaseService.updateSale(editingSale.id, saleData, invoiceFile);
        toast.success('Sale updated successfully');
      } else {
        await firebaseService.addSale(saleData, invoiceFile);
        toast.success('Sale added successfully');
      }

      setShowForm(false);
      resetForm();
      loadSales();
    } catch (error) {
      console.error('Error saving sale:', error);
      toast.error('Failed to save sale');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (sale) => {
    setEditingSale(sale);
    setFormData({
      saleDate: sale.saleDate || new Date(),
      customerName: sale.customerName || '',
      invoiceNumber: sale.invoiceNumber || '',
      saleAmount: sale.totalAmount?.toString() || '',
      isGstInclusive: sale.isGstInclusive !== false, // default to true if not specified
      paymentMethod: sale.paymentMethod || 'Cash',
      description: sale.description || '',
      productName: sale.productName || '',
      imeiNumber: sale.imeiNumber || '',
      customerPhone: sale.customerPhone || '',
      customerEmail: sale.customerEmail || '',
      selectedProductId: sale.selectedProductId || '',
      discount: sale.discount || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (saleId) => {
    if (!hasPermission('delete_records')) {
      toast.error('You do not have permission to delete records');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this sale?')) {
      return;
    }

    try {
      await firebaseService.deleteSale(saleId);
      toast.success('Sale deleted successfully');
      loadSales();
    } catch (error) {
      console.error('Error deleting sale:', error);
      toast.error('Failed to delete sale');
    }
  };

  const handleGenerateInvoice = async (sale) => {
    try {
      const invoiceData = {
        saleDate: sale.saleDate,
        customerName: sale.customerName || 'Customer',
        invoiceNumber: sale.invoiceNumber || `INV-${Date.now()}`,
        saleAmount: sale.baseAmount || 0,
        gstAmount: sale.gstAmount || 0,
        paymentMethod: sale.paymentMethod,
        description: sale.description || 'Product/Service',
        productName: sale.productName || '',
        imeiNumber: sale.imeiNumber || '',
        customerPhone: sale.customerPhone || '',
        customerEmail: sale.customerEmail || '',
        discount: sale.discount || 0
      };
      
      const result = await generateInvoice(invoiceData, currentUser.uid);
      toast.success(`Invoice ${result.filename} generated and saved successfully!`);
      
      // Update the sale record with invoice URL if available
      if (result.downloadUrl) {
        try {
          await firebaseService.updateSale(sale.id, { invoiceUrl: result.downloadUrl });
        } catch (error) {
          console.error('Error updating sale with invoice URL:', error);
        }
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error('Failed to generate invoice');
    }
  };

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file && file.type === 'application/pdf') {
      setInvoiceFile(file);
    } else {
      toast.error('Please upload a PDF file only');
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1
  });

  const calculatePreview = () => {
    if (formData.saleAmount) {
      return calculateOutputGST(formData.saleAmount, formData.isGstInclusive, formData.discount);
    }
    return null;
  };

  const previewData = calculatePreview();

  // Handle product selection - DON'T AUTO-FILL SALE AMOUNT
  const handleProductSelect = (productId) => {
    const selectedProduct = availableProducts.find(p => p.id === productId);
    if (selectedProduct) {
      setFormData({
        ...formData,
        selectedProductId: productId,
        productName: selectedProduct.productName,
        imeiNumber: selectedProduct.imeiNumber,
        // saleAmount: selectedProduct.purchaseAmount?.toString() || '', // REMOVED - Don't auto-fill
        description: selectedProduct.description || ''
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Sales
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage your sales transactions and track output GST
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            type="button"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="btn-primary"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Sale
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quarter</label>
            <select
              value={filters.quarter}
              onChange={(e) => setFilters({ ...filters, quarter: e.target.value })}
              className="input"
            >
              <option value="">All Quarters</option>
              <option value="Q1">Q1 (Apr-Jun)</option>
              <option value="Q2">Q2 (Jul-Sep)</option>
              <option value="Q3">Q3 (Oct-Dec)</option>
              <option value="Q4">Q4 (Jan-Mar)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: e.target.value })}
              className="input"
            >
              <option value="">All Years</option>
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - 2 + i;
                return (
                  <option key={year} value={year}>{year}-{(year + 1).toString().substr(-2)}</option>
                );
              })}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
            <select
              value={filters.paymentMethod}
              onChange={(e) => setFilters({ ...filters, paymentMethod: e.target.value })}
              className="input"
            >
              <option value="">All Payment Methods</option>
              {paymentMethods.map(method => (
                <option key={method} value={method}>{method}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Sale Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingSale ? 'Edit Sale' : 'Add New Sale'}
                </h3>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sale Date *
                    </label>
                    <DatePicker
                      selected={formData.saleDate}
                      onChange={(date) => setFormData({ ...formData, saleDate: date })}
                      className="input w-full"
                      dateFormat="dd/MM/yyyy"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      value={formData.customerName}
                      onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                      className="input"
                      placeholder="Enter customer name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customer Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.customerPhone}
                      onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                      className="input"
                      placeholder="+91 XXXXXXXXXX"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customer Email (Optional)
                    </label>
                    <input
                      type="email"
                      value={formData.customerEmail}
                      onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                      className="input"
                      placeholder="customer@email.com (optional)"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Invoice Number
                    </label>
                    <input
                      type="text"
                      value={formData.invoiceNumber}
                      onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                      className="input"
                      placeholder="Optional"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Method
                    </label>
                    <select
                      value={formData.paymentMethod}
                      onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                      className="input"
                    >
                      {paymentMethods.map(method => (
                        <option key={method} value={method}>{method}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sale Amount *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.saleAmount}
                      onChange={(e) => setFormData({ ...formData, saleAmount: e.target.value })}
                      className="input"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Discount (₹)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.discount}
                      onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                      className="input"
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      GST Calculation
                    </label>
                    <div className="mt-2">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          className="form-radio"
                          name="gstType"
                          checked={formData.isGstInclusive}
                          onChange={() => setFormData({ ...formData, isGstInclusive: true })}
                        />
                        <span className="ml-2">GST Inclusive</span>
                      </label>
                      <label className="inline-flex items-center ml-6">
                        <input
                          type="radio"
                          className="form-radio"
                          name="gstType"
                          checked={!formData.isGstInclusive}
                          onChange={() => setFormData({ ...formData, isGstInclusive: false })}
                        />
                        <span className="ml-2">GST Exclusive</span>
                      </label>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Product from Inventory *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search products by name or IMEI..."
                      className="input pr-10"
                      value={productSearchTerm}
                      onChange={(e) => {
                        const searchTerm = e.target.value;
                        setProductSearchTerm(searchTerm);
                        
                        if (searchTerm.trim() === '') {
                          setAvailableProducts(allProducts);
                        } else {
                          const filtered = allProducts.filter(product => 
                            product.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            product.imeiNumber.includes(searchTerm)
                          );
                          setAvailableProducts(filtered);
                        }
                      }}
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                  <select
                    value={formData.selectedProductId}
                    onChange={(e) => handleProductSelect(e.target.value)}
                    className="input mt-2"
                    required
                  >
                    <option value="">Choose a product...</option>
                    {availableProducts.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.productName} - IMEI: {product.imeiNumber} (₹{product.purchaseAmount})
                      </option>
                    ))}
                  </select>
                  {availableProducts.length === 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      No products available. Add products through Purchases first.
                    </p>
                  )}
                </div>
                
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product Name
                    </label>
                    <input
                      type="text"
                      value={formData.productName}
                      readOnly
                      className="input bg-gray-50"
                      placeholder="Auto-filled from selection"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      IMEI Number
                    </label>
                    <input
                      type="text"
                      value={formData.imeiNumber}
                      readOnly
                      className="input bg-gray-50"
                      placeholder="Auto-filled from selection"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input"
                    rows="2"
                    placeholder="Additional product details, warranty info, etc."
                  />
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice PDF (Optional)
                  </label>
                  <div
                    {...getRootProps()}
                    className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md cursor-pointer hover:border-gray-400 ${
                      isDragActive ? 'border-primary-400 bg-primary-50' : ''
                    }`}
                  >
                    <div className="space-y-1 text-center">
                      <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <input {...getInputProps()} />
                        <p>
                          {invoiceFile
                            ? `Selected: ${invoiceFile.name}`
                            : isDragActive
                            ? 'Drop the PDF file here...'
                            : 'Drag and drop a PDF file here, or click to select'
                          }
                        </p>
                      </div>
                      <p className="text-xs text-gray-500">PDF up to 10MB</p>
                    </div>
                  </div>
                </div>

                {/* Calculation Preview */}
                {previewData && (
                  <div className="bg-gray-50 p-4 rounded-md">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">GST Calculation Preview</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Base Amount:</span>
                        <span className="ml-2 font-medium">{formatCurrency(previewData.baseAmount)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">IGST (18%):</span>
                        <span className="ml-2 font-medium text-red-600">{formatCurrency(previewData.igst)}</span>
                      </div>
                      {previewData.discount > 0 && (
                        <div>
                          <span className="text-gray-600">Discount:</span>
                          <span className="ml-2 font-medium text-green-600">-{formatCurrency(previewData.discount)}</span>
                        </div>
                      )}
                      <div className="col-span-2 pt-2 border-t border-gray-200">
                        <span className="text-gray-600">Total Amount:</span>
                        <span className="ml-2 font-medium text-lg">{formatCurrency(previewData.totalAmount)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary"
                  >
                    {submitting ? (
                      <div className="spinner mr-2"></div>
                    ) : null}
                    {editingSale ? 'Update' : 'Add'} Sale
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Sales Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Sales Records</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            {sales.length} sale{sales.length !== 1 ? 's' : ''} found
          </p>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : sales.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Product</th>
                  <th>IMEI</th>
                  <th>Invoice No.</th>
                  <th>Base Amount</th>
                  <th>GST Amount</th>
                  <th>Total Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {sales.map((sale) => (
                  <tr key={sale.id}>
                    <td>
                      {sale.saleDate
                        ? new Date(sale.saleDate).toLocaleDateString('en-IN')
                        : 'N/A'
                      }
                    </td>
                    <td className="font-medium">{sale.customerName || 'Cash Customer'}</td>
                    <td className="font-medium">{sale.productName || '-'}</td>
                    <td className="font-mono text-sm">{sale.imeiNumber || '-'}</td>
                    <td>{sale.invoiceNumber || '-'}</td>
                    <td>{formatCurrency(sale.baseAmount || 0)}</td>
                    <td className="text-red-600 font-medium">
                      {formatCurrency(sale.gstAmount || 0)}
                    </td>
                    <td className="font-medium">{formatCurrency(sale.totalAmount || 0)}</td>
                    <td>
                      <div className="flex items-center space-x-2">
                        {sale.invoiceUrl && (
                          <a
                            href={sale.invoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-900"
                            title="View Invoice"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </a>
                        )}
                        <button
                          onClick={() => handleGenerateInvoice(sale)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Generate PDF Invoice"
                        >
                          <DocumentTextIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(sale)}
                          className="text-gray-600 hover:text-gray-900"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        {hasPermission('delete_records') && (
                          <button
                            onClick={() => handleDelete(sale.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <CurrencyRupeeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No sales</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first sale.</p>
            <div className="mt-6">
              <button
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className="btn-primary"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Sale
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Sales;
